import { normalizeTheme } from '../utils/theme.js';

export const METADATA_STUDIO_THEMES = Object.freeze([
  'light',
  'dark'
]);

const STUDIO_ASSET_ROOT = '/home-redesign/studio';

const CAPTURE_DEFINITIONS = Object.freeze({
  overview: {
    width: 1440,
    height: 900,
    widths: [640, 960, 1440],
    alt: 'Metadata Studio workspace with upload controls, workflow guidance, and marketplace-ready output options',
    benefit: 'Shows the complete, current Metadata Studio workspace before a batch begins.'
  },
  batch: {
    width: 960,
    height: 553,
    widths: [640, 960],
    alt: 'Metadata Studio processing a fourteen-file batch with titles, descriptions, keywords, and live statuses',
    benefit: 'Proves that mixed files can be processed and reviewed in one visible queue.'
  },
  keywords: {
    width: 960,
    height: 553,
    widths: [640, 960],
    alt: 'Metadata Studio batch list showing generated titles, descriptions, keyword chips, and completed statuses',
    benefit: 'Makes generated metadata and keyword results readable instead of using a decorative dashboard.'
  },
  review: {
    width: 960,
    height: 553,
    widths: [640, 960],
    alt: 'Metadata Studio review workspace with a large asset preview and editable title, description, and keyword inspector',
    benefit: 'Shows that generated metadata remains editable beside the source asset.'
  },
  export: {
    width: 960,
    height: 441,
    widths: [640, 960],
    alt: 'Metadata Studio review and export controls with a completed batch ready for delivery',
    benefit: 'Connects reviewed metadata to the final export step.'
  }
});

const buildCapture = (theme, captureId) => {
  const definition = CAPTURE_DEFINITIONS[captureId];
  const fileBase = `${STUDIO_ASSET_ROOT}/${theme}/studio-${captureId}`;
  const srcSet = definition.widths
    .map((width) => `${fileBase}-${width}.webp ${width}w`)
    .join(', ');

  return Object.freeze({
    id: captureId,
    theme,
    src: `${fileBase}-${definition.widths.at(-1)}.webp`,
    srcSet,
    mobileSrc: `${fileBase}-${definition.widths[0]}.webp`,
    desktopSrc: `${fileBase}-${definition.widths.at(-1)}.webp`,
    width: definition.width,
    height: definition.height,
    alt: definition.alt,
    benefit: definition.benefit,
    format: 'image/webp',
    provenance: 'Current guest-accessible Metadata Studio demo captured in the named real theme.'
  });
};

const SCENE_CAPTURE = Object.freeze({
  upload: 'batch',
  analysis: 'review',
  title: 'review',
  description: 'review',
  keywords: 'keywords',
  batch: 'batch',
  platform: 'export',
  export: 'export'
});

export const METADATA_STUDIO_ASSETS = Object.freeze(Object.fromEntries(
  METADATA_STUDIO_THEMES.map((theme) => [
    theme,
    Object.freeze({
      hero: buildCapture(theme, 'overview'),
      megaMenu: buildCapture(theme, 'overview'),
      finalCta: buildCapture(theme, 'export'),
      captures: Object.freeze(Object.fromEntries(
        Object.keys(CAPTURE_DEFINITIONS).map((captureId) => [
          captureId,
          buildCapture(theme, captureId)
        ])
      )),
      scenes: Object.freeze(Object.fromEntries(
        Object.entries(SCENE_CAPTURE).map(([sceneId, captureId]) => [
          sceneId,
          buildCapture(theme, captureId)
        ])
      ))
    })
  ])
));

const resolveMetadataStudioAssetTheme = (theme) => (
  normalizeTheme(theme) === 'light' ? 'light' : 'dark'
);

export const getMetadataStudioThemeAssets = (theme) => (
  METADATA_STUDIO_ASSETS[resolveMetadataStudioAssetTheme(theme)]
);

export const getMetadataStudioSceneAsset = (theme, sceneId) => (
  getMetadataStudioThemeAssets(theme).scenes[sceneId] || null
);

export default METADATA_STUDIO_ASSETS;
